import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Modal, ModalBody, ModalFooter, ModalHeader, Text, VFlow } from 'bold-ui'
import { Form, FormRenderProps } from 'components/form'
import { information } from 'components/modals/information'
import { ImovelDto } from 'graphql/types.generated'
import { useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { LogradouroCadastroDuplicadoFormModel } from 'view/cadastro-imovel/model-cadastroImovel'

import { ImovelDuplicadoCard } from '../ImovelDuplicadoCard'

interface LogradouroCadastroDuplicadoModalProps {
  isEnderecoIndigena: boolean
  isModalOpen: boolean
  onClose(): void
  imoveis: Array<ImovelDto>
  deleteCacheState(): void
}

export function LogradouroCadastroDuplicadoModal(props: LogradouroCadastroDuplicadoModalProps) {
  const { isEnderecoIndigena, isModalOpen, onClose, imoveis, deleteCacheState } = props
  const [selectedImovelIndex, setSelectedImovelIndex] = useState(null)

  const match = useRouteMatch()
  const history = useHistory()

  const imovel = imoveis[0]
  const initialValues = {
    logradouro: imovel?.logradouro,
    numero: imovel?.numero,
    complemento: imovel?.complemento,
    dsei: imovel?.dsei,
    poloBase: imovel?.poloBase,
    numeroResidenciaIndigena: imovel?.numeroResidenciaIndigena,
    imoveis: imoveis,
  }

  const handleSubmit = () => {
    setTimeout(() => {
      deleteCacheState()
      history.push(
        `${match.url.replace('cadastrar', 'editar')}/${
          imoveis[selectedImovelIndex].id
        }?redirect=informacoes&callbackUrl=${match.url}`
      )
    })
  }

  const handleInfoNaoDuplicarCadastroModal = () => {
    information({
      type: 'primary',
      title: 'Preencha com atenção!',
      body: isEnderecoIndigena
        ? 'Revise os campos já preenchidos e realize alguma alteração para não duplicar o cadastro.'
        : 'Revise os campos já preenchidos e altere, no mínimo, o campo de Complemento, para não duplicar o cadastro.',
      depthLevel: 2,
      manageOverflow: false,
      showCloseButton: true,
      closeLabel: 'Entendi',
    })()
  }

  const handleClose = () => {
    handleInfoNaoDuplicarCadastroModal()
    onClose()
  }

  const renderForm = (formProps: FormRenderProps<LogradouroCadastroDuplicadoFormModel>) => {
    const { values } = formProps
    const imoveisCount = values.imoveis.length

    return (
      <Modal
        size='auto'
        open={isModalOpen}
        closeOnBackdropClick={false}
        onClose={handleClose}
        style={{ width: '45rem' }}
      >
        <ModalHeader
          title={pluralizeTitle(imoveisCount)}
          subtitle={pluralizeAtualizacaoCadastroMessage(imoveisCount)}
        />
        <ModalBody>
          <Grid>
            <Cell size={12}>
              <Text fontWeight='bold'>{pluralizeDescription(imoveisCount, values, isEnderecoIndigena)}</Text>
            </Cell>
            <Cell size={12}>
              <VFlow vSpacing={1}>
                {values.imoveis.map((imovel, index) => (
                  <ImovelDuplicadoCard
                    key={index}
                    imovel={imovel}
                    isSelected={index === selectedImovelIndex}
                    onSelectImovel={() => setSelectedImovelIndex(index)}
                  />
                ))}
              </VFlow>
            </Cell>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button kind='normal' size='medium' onClick={handleClose}>
              Cadastro novo
            </Button>
            <Button
              kind='primary'
              style={css`
                width: 8rem;
              `}
              onClick={handleSubmit}
              disabled={selectedImovelIndex === null}
            >
              Atualizar
            </Button>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }
  return (
    <Form<LogradouroCadastroDuplicadoFormModel>
      onSubmit={handleSubmit}
      render={renderForm}
      initialValues={initialValues}
    />
  )
}

const pluralizeTitle = (numeroImoveisDuplicados: number) => {
  return numeroImoveisDuplicados > 1 ? 'Encontramos imóveis com as mesmas informações' : 'Imóvel já cadastrado'
}

const pluralizeDescription = (
  numeroImoveisDuplicados: number,
  values: LogradouroCadastroDuplicadoFormModel,
  isEnderecoIndigena: boolean
) => {
  const prefix = numeroImoveisDuplicados > 1 ? 'Imóveis encontrados' : 'Imóvel encontrado'

  if (isEnderecoIndigena) {
    const enderecoIndigena = [
      values.dsei?.nome?.titleCase(),
      values.poloBase?.nome?.titleCase(),
      values.numeroResidenciaIndigena,
    ]
      .filter(Boolean)
      .join(', ')

    return enderecoIndigena ? `${prefix} para "${enderecoIndigena}."` : `${prefix}.`
  } else {
    const logradouroNumero = [values.logradouro?.titleCase(), values.numero].filter(Boolean).join(', ')

    const endereco = [logradouroNumero, values.complemento?.titleCase()].filter(Boolean).join(' ')

    return endereco ? `${prefix} para "${endereco}."` : `${prefix}.`
  }
}

const pluralizeAtualizacaoCadastroMessage = (numeroImoveisDuplicados: number) => {
  return numeroImoveisDuplicados > 1
    ? `Já existem outros cadastros de imóveis com estas mesmas informações de identificação. Para evitar duplicação de cadastro você pode escolher um deles na lista abaixo para atualizar.`
    : `Já existe um cadastro de imóvel com estas mesmas informações de identificação. Informe se deseja cadastrar um novo imóvel ou atualizar este que já existe.`
}
